const toNumberSafe = (value) => {
  if (value == null) {
    return null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

export const computeDelta = (series) => {
  if (!Array.isArray(series) || series.length === 0) {
    return [];
  }
  return series.map((value, index) => {
    if (index === 0) {
      return null;
    }
    const current = toNumberSafe(value);
    const previous = toNumberSafe(series[index - 1]);
    if (current == null || previous == null) {
      return null;
    }
    return current - previous;
  });
};

export const computeReturns = (priceSeries, horizon) => {
  if (!Array.isArray(priceSeries) || priceSeries.length === 0) {
    return [];
  }
  const step = Math.max(1, Number(horizon) || 0);
  return priceSeries.map((price, index) => {
    const start = toNumberSafe(price);
    const end = toNumberSafe(priceSeries[index + step]);
    if (start == null || end == null || start <= 0 || end <= 0) {
      return null;
    }
    return Math.log(end / start);
  });
};

const computePearson = (pairs) => {
  if (pairs.length < 2) {
    return null;
  }
  const xs = pairs.map((pair) => pair[0]);
  const ys = pairs.map((pair) => pair[1]);
  const meanX = xs.reduce((sum, value) => sum + value, 0) / xs.length;
  const meanY = ys.reduce((sum, value) => sum + value, 0) / ys.length;
  let numerator = 0;
  let denomX = 0;
  let denomY = 0;
  for (let i = 0; i < xs.length; i += 1) {
    const dx = xs[i] - meanX;
    const dy = ys[i] - meanY;
    numerator += dx * dy;
    denomX += dx * dx;
    denomY += dy * dy;
  }
  const denominator = Math.sqrt(denomX * denomY);
  if (!denominator) {
    return null;
  }
  return numerator / denominator;
};

const rankSeries = (series) => {
  const sorted = series
    .map((value, index) => ({ value, index }))
    .sort((a, b) => a.value - b.value);
  const ranks = Array(series.length).fill(0);
  let i = 0;
  while (i < sorted.length) {
    let j = i;
    while (j < sorted.length && sorted[j].value === sorted[i].value) {
      j += 1;
    }
    const avgRank = (i + j - 1) / 2 + 1;
    for (let k = i; k < j; k += 1) {
      ranks[sorted[k].index] = avgRank;
    }
    i = j;
  }
  return ranks;
};

export const computeLaggedCorrelation = (deltaSeries, returnSeries, lag, method = 'pearson') => {
  if (!Array.isArray(deltaSeries) || !Array.isArray(returnSeries)) {
    return { correlation: null, sampleSize: 0 };
  }
  const shift = Number(lag) || 0;
  const pairs = [];
  for (let i = 0; i < deltaSeries.length; i += 1) {
    const j = i + shift;
    if (j < 0 || j >= returnSeries.length) {
      continue;
    }
    const x = toNumberSafe(deltaSeries[i]);
    const y = toNumberSafe(returnSeries[j]);
    if (x == null || y == null) {
      continue;
    }
    pairs.push([x, y]);
  }
  if (pairs.length < 2) {
    return { correlation: null, sampleSize: pairs.length };
  }
  if (method === 'spearman') {
    const xs = pairs.map((pair) => pair[0]);
    const ys = pairs.map((pair) => pair[1]);
    const rankedPairs = rankSeries(xs).map((rank, index) => [rank, rankSeries(ys)[index]]);
    return {
      correlation: computePearson(rankedPairs),
      sampleSize: pairs.length,
    };
  }
  return {
    correlation: computePearson(pairs),
    sampleSize: pairs.length,
  };
};

export const getCorrelationStrengthBucket = (value) => {
  const magnitude = Math.abs(Number(value) || 0);
  if (magnitude < 0.2) {
    return { label: 'Very weak relationship', range: '|ρ| < 0.20' };
  }
  if (magnitude < 0.4) {
    return { label: 'Weak relationship', range: '|ρ| < 0.40' };
  }
  if (magnitude < 0.6) {
    return { label: 'Moderate relationship', range: '|ρ| < 0.60' };
  }
  return { label: 'Strong relationship', range: '|ρ| ≥ 0.60' };
};

export const getCorrelationInterpretation = (lag) => {
  if (lag < 0) {
    return 'Interpretation: Price moves first; positioning adjusts later (reactive flows).';
  }
  if (lag > 0) {
    return 'Interpretation: Positioning changes tend to lead future price moves.';
  }
  return 'Interpretation: Same-date relationship. Interpret with caution.';
};

export const hasConsistentSignAcrossLags = (sparklineData, targetLag, windowSize = 3) => {
  if (!Array.isArray(sparklineData) || sparklineData.length === 0) {
    return false;
  }
  const index = sparklineData.findIndex((point) => point.lag === targetLag);
  if (index < 0) {
    return false;
  }
  const targetValue = sparklineData[index]?.rho;
  if (targetValue == null) {
    return false;
  }
  const targetSign = Math.sign(targetValue);
  if (!targetSign) {
    return false;
  }
  const startMin = Math.max(0, index - windowSize + 1);
  const startMax = Math.min(index, sparklineData.length - windowSize);
  for (let start = startMin; start <= startMax; start += 1) {
    let consistent = true;
    for (let offset = 0; offset < windowSize; offset += 1) {
      const point = sparklineData[start + offset];
      if (!point || point.rho == null || Math.sign(point.rho) !== targetSign) {
        consistent = false;
        break;
      }
    }
    if (consistent) {
      return true;
    }
  }
  return false;
};

export const getConfidenceBadge = (correlationValue, sampleSize, sparklineData, lag) => {
  const value = Number(correlationValue);
  const absValue = Number.isFinite(value) ? Math.abs(value) : 0;
  let score = 0;

  if (absValue >= 0.3) {
    score += 1;
  }
  if (sampleSize >= 40) {
    score += 1;
  }
  if (hasConsistentSignAcrossLags(sparklineData, lag, 3)) {
    score += 1;
  }
  if (absValue < 0.2) {
    score -= 1;
  }
  if (sampleSize < 25) {
    score -= 1;
  }

  let label = 'Low confidence';
  if (score >= 3) {
    label = 'High confidence';
  } else if (score >= 1) {
    label = 'Moderate confidence';
  }
  return { label, score };
};
